import React, { useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import InputMask from 'react-input-mask';
import Paper from 'components/Paper';
import TextField from 'components/TextField';
import Button from 'components/Button';
import Images from 'assets/img';
import InsurancesActions from 'store/ducks/insurances';
import { Container, FormContent, Section, SectionTitle, WrapperInput, Row, HeaderButtons } from './styles';

// Componente wrapper para InputMask que preserva a posição do cursor (igual ao usado em ViagemForm)
const MaskedInput = ({ mask, value, onChange, onBlur, children, ...props }) => {
  const handleChange = (e) => {
    const input = e.target;
    const cursorPosition = input.selectionStart;
    const oldValue = value || '';
    const newValue = input.value || '';
    
    onChange(e);
    
    setTimeout(() => {
      if (input && input.setSelectionRange) {
        const lengthDiff = newValue.length - oldValue.length;
        let newPosition = cursorPosition;
        
        if (lengthDiff > 0) {
          newPosition = Math.min(cursorPosition + lengthDiff, newValue.length);
        } else if (lengthDiff < 0) {
          newPosition = Math.max(0, cursorPosition + lengthDiff);
        }
        
        newPosition = Math.min(newPosition, newValue.length);
        input.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  const handleBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <InputMask
      {...props}
      mask={mask}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      alwaysShowMask={false}
      maskChar={null}
    >
      {children}
    </InputMask>
  );
};

function InsuranceForm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const { insurances } = useSelector((state) => state.insurances);
  const isEdit = !!id;
  const formRef = useRef(null);

  const insurance = isEdit ? insurances.find((i) => i.id === id) : null;

  useEffect(() => {
    if (isEdit && !insurance) {
      dispatch(InsurancesActions.getInsurancesRequest());
    }
  }, [dispatch, isEdit, insurance]);

  const initialValues = {
    codigo: insurance?.codigo || '',
    nome_seguradora: insurance?.nome_seguradora || '',
    cnpj_seguradora: insurance?.cnpj_seguradora || '',
    numero_apolice: insurance?.numero_apolice || '',
  };

  const handleSubmit = (values) => {
    // Adicionar empresa_id se houver empresa selecionada
    const dadosComEmpresa = { ...values };
    try {
      const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany') || 'null');
      if (selectedCompany?.id) {
        dadosComEmpresa.empresa_id = selectedCompany.id;
      }
    } catch (error) {
      console.error('Erro ao obter empresa selecionada:', error);
    }
    
    if (isEdit) {
      dispatch(InsurancesActions.updateInsuranceRequest(id, dadosComEmpresa, () => history.push('/insurances')));
    } else {
      dispatch(InsurancesActions.addInsuranceRequest(dadosComEmpresa, () => history.push('/insurances')));
    }
  };

  return (
    <Container>
      <Paper
        title={isEdit ? 'Editar Seguro' : 'Novo Seguro'}
        icon={Images.IconPaper}
        rightContent={
          <HeaderButtons>
            <Button
              label="Voltar"
              onClick={() => history.push('/insurances')}
              type="button"
              margin="0 10px 0 0"
            />
            <Button
              label={isEdit ? 'Atualizar' : 'Criar'}
              onClick={() => {
                if (formRef.current) {
                  formRef.current.handleSubmit();
                }
              }}
              type="button"
            />
          </HeaderButtons>
        }
      >
        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, handleChange, errors, touched }) => (
            <form>
              <FormContent>
                <Section>
                  <SectionTitle>Dados do Seguro</SectionTitle>
                  
                  <Row>
                    <WrapperInput>
                      <TextField
                        label="Código *"
                        name="codigo"
                        value={values.codigo}
                        onChange={handleChange}
                        error={touched.codigo && Boolean(errors.codigo)}
                        helperText={touched.codigo && errors.codigo}
                        fullWidth
                        color="secondary"
                      />
                    </WrapperInput>

                    <WrapperInput>
                      <TextField
                        label="Nome da Seguradora *"
                        name="nome_seguradora"
                        value={values.nome_seguradora}
                        onChange={handleChange}
                        error={touched.nome_seguradora && Boolean(errors.nome_seguradora)}
                        helperText={touched.nome_seguradora && errors.nome_seguradora}
                        fullWidth
                        color="secondary"
                      />
                    </WrapperInput>
                  </Row>

                  <Row>
                    <WrapperInput>
                      <MaskedInput
                        mask="99.999.999/9999-99"
                        value={values.cnpj_seguradora}
                        onChange={handleChange}
                        name="cnpj_seguradora"
                      >
                        {(inputProps) => (
                          <TextField
                            {...inputProps}
                            label="CNPJ da Seguradora *"
                            error={touched.cnpj_seguradora && Boolean(errors.cnpj_seguradora)}
                            helperText={touched.cnpj_seguradora && errors.cnpj_seguradora}
                            fullWidth
                            color="secondary"
                          />
                        )}
                      </MaskedInput>
                    </WrapperInput>

                    <WrapperInput>
                      <TextField
                        label="Número da Apólice *"
                        name="numero_apolice"
                        value={values.numero_apolice}
                        onChange={handleChange}
                        error={touched.numero_apolice && Boolean(errors.numero_apolice)}
                        helperText={touched.numero_apolice && errors.numero_apolice}
                        fullWidth
                        color="secondary"
                      />
                    </WrapperInput>
                  </Row>
                </Section>
              </FormContent>
            </form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
}

export default InsuranceForm;

